import { z } from 'zod';

/** First frame on every SSE stream (written by beginSseStream). Bare payload —
 *  no `type` or `timestamp` — because EventSource can't read response headers
 *  and this predates any route logic. */
export const DeployVersionEventSchema = z.object({ version: z.string() });

// Optimization state. This schema is now the sole source of truth for the
// shape (src/server/types.ts re-exports these as type aliases from
// contracts/dist for backward-compat internal imports); shared by both the
// turn stream and the conversation stream's optimization_* events.
// value/delta/contribution_percent nullable: the Optiland track
// (analysis-service/services/optimization_core.py extract_problem_state, in
// its per-operand loop) emits null for all three when an operand is
// all-vignetted (fun_val is None) — `delta` alone is additionally null when
// the operand's weight is falsy (contribution_percent is 0.0 there, not
// null). The Zemax track sends null for a readback that has no number.
// `unit` optional: only the Optiland emitter sets it (always present there,
// `_OPERAND_UNITS.get(op.operand_type, 'unknown')`); the Zemax-track operand
// objects (zemaxOptimizeUtils.ts OptimizationOperandSchema, and the
// Zemax-only operand_results in sseConversation.ts) never include it.
export const OptimizationOperandSchema = z.object({
  type: z.string(),
  target: z.number().nullable(), // Equality constraint (target=X)
  min_val: z.number().nullable(), // Inequality constraint (value >= min_val)
  max_val: z.number().nullable(), // Inequality constraint (value <= max_val)
  value: z.number().nullable(),
  delta: z.number().nullable(),
  /** Nullable for the same reason `target` is: MFE structural rows (BLNK,
   *  DMFS) carry no weight in OpticStudio, and a row whose Weight cell reads
   *  back non-finite has none either. 0 is a weight a row can legitimately
   *  hold (a monitor row parked at 0 reports a value without contributing),
   *  so coercing the absent case to 0 made the two indistinguishable — see
   *  `_format_operand_results` in
   *  zemax-analysis-service/routers/optimization.py. The Optiland emitter
   *  reaches null here too: `_to_item(op.weight)` (optimization_core.py)
   *  maps None and non-finite weights to None, exactly as it does for the
   *  value/delta/contribution_percent above. */
  weight: z.number().nullable(),
  contribution_percent: z.number().nullable(),
  unit: z.string().optional(),
  /** True when the worker could not read this operand's post-op value. A
   *  `value` or `delta` sent alongside it is not a reading, whatever its
   *  number. Optional so older backends without the field stay compatible
   *  (default = false in consumers). */
  readback_failed: z.boolean().optional(),
  // Optional context for field/wavelength/pupil/surface-specific operands
  // (optimization_core.py _operand_context, spread into each operand by
  // extract_problem_state, which sets the auto_injected flag on the line just
  // above that spread) — the UI renders pupil/fan labels
  // (formatOperandContext in frontend/components/OptimizationViewer.tsx).
  field_x: z.number().optional(),
  field_y: z.number().optional(),
  pupil_x: z.number().optional(),
  pupil_y: z.number().optional(),
  fan_axis: z.string().optional(),
  wavelength: z.union([z.number(), z.string()]).optional(),
  surface_number: z.number().optional(),
  seidel_number: z.number().optional(),
  auto_injected: z.boolean().optional(),
});

export const OptimizationVariableSchema = z.object({
  type: z.string(),
  surface: z.number(),
  /** Number for continuous (radius/thickness/conic/param) variables — null when
   *  the post-opt readback returned a non-finite value (e.g. an infinity-radius/
   *  thickness variable on a flat or afocal surface); the Mac router coerces
   *  inf/NaN here so the wire ships explicit null rather than the "Infinity"
   *  sentinel string used for LLM-JSON surface geometry. String (or null) for
   *  `type: 'material'` variables, which report a glass name instead of a
   *  numeric value (analysis-service/services/optimization_core.py
   *  _serialize_variable, ~lines 111-112: `str(var.value) if var.value else
   *  None`). */
  value: z.union([z.number(), z.string()]).nullable(),
  /** Only continuous variables carry bounds — material variables omit `min`/
   *  `max` entirely (_serialize_variable, ~lines 111-116), so both are
   *  optional as well as nullable. */
  min: z.number().nullable().optional(),
  max: z.number().nullable().optional(),
  /** Optiland track emits it for its own live problem variables
   *  (_VARIABLE_UNITS.get(var.type, 'unknown'), including material
   *  variables) — but NOT for the `extra_variables` GlassExpert splices
   *  final_state["variables"] with in build_complete_event
   *  (problem_builder.py:322-329): those material wrappers were removed
   *  from problem.variables before final polishing and are serialized by
   *  hand there, without a `unit` key (and with explicit `min: None,
   *  max: None`). The Zemax router's variables omit it entirely
   *  (zemax-analysis-service/routers/optimization.py builds only
   *  type/surface/value/min/max). */
  unit: z.string().optional(),
  /** Optiland track only, and only on the variables that have one: which
   *  coefficient of a multi-coefficient surface this row varies. A `material`
   *  variable returns before the key is ever set; a continuous variable gets it
   *  only when the underlying optiland variable carries a `coeff_number`
   *  attribute, and a `qcon_coeff` variable always gets it — that branch also
   *  rewrites `type` to `asphere_coeff` and reports the index in the monomial
   *  representation (`qcon_standard_view` maps Q-con order q to 2q+1) rather
   *  than the optimizer-internal Q-con order (analysis-service/services/
   *  optimization_core.py `_serialize_variable`). The Zemax router's variables
   *  omit it entirely (zemax-analysis-service/routers/optimization.py builds
   *  only type/surface/value/min/max). Without it, every coefficient variable
   *  on one surface is an indistinguishable `asphere_coeff / S{n}` row. */
  coeff_number: z.number().optional(),
});

/** current/initial nullable: the Zemax track's `merit_unknown` path
 *  (zemax-analysis-service/routers/optimization.py:865-866, feeding
 *  `_build_optimization_state`'s `merit.current`/`merit.initial` straight
 *  from `merit_after`/`merit_before`, which are `float | None` — None when
 *  OpticStudio's CalculateMeritFunction returned NaN/Inf at MFE init or
 *  post-opt). The Optiland emitter never actually sends null here:
 *  optimization_core.py `extract_problem_state` coerces a None `current_merit`
 *  to 0.0, and a None/absent `initial_merit` to `current_merit`, before
 *  building the merit dict. */
export const OptimizationMeritSchema = z.object({
  current: z.number().nullable(),
  initial: z.number().nullable(),
  /** Null when no percentage exists: a merit is unknown, or `initial` is 0. */
  improvement_percent: z.number().nullable(),
});

export const OptimizationStateSchema = z.object({
  elapsed_seconds: z.number(),
  merit: OptimizationMeritSchema,
  operands: z.array(OptimizationOperandSchema),
  variables: z.array(OptimizationVariableSchema),
  /** Merit samples from the worker's recorder thread (Zemax track; t = seconds
   *  since sampling started). On progress events this is the series so far; on
   *  the complete event it is the whole run, harvested worker-side before the
   *  recorder file is cleaned up — which is the only source for a run that
   *  finished inside a single heartbeat and was never polled. This schema is
   *  the canonical source for the shape; frontend/hooks/useConversationStream.ts
   *  consumes OptimizationState (and the other conversation-stream event types)
   *  from contracts/dist. */
  merit_history: z.array(z.object({ t: z.number(), merit: z.number() })).optional(),
  /** CODE V track (codevOptimize): the AUT block the run sent and the budget the
   *  worker bounds it with (MXC, and the wall time at which it stops GO). */
  codev_aut: z.object({ lines: z.array(z.string()), max_cycles: z.number().int(), max_seconds: z.number().int() }).optional(),
  /** True when the recorder's series stopped advancing (~30s without a new
   *  sample) while the run continues — the curve is frozen, not converged. */
  sampling_stalled: z.boolean().optional(),
  /** Optiland init-state only: `initial_problem_state` stashes the route's
   *  accumulated warnings (missing_glass_fence, auto-bounds, no-op-merit)
   *  onto the `optimization_start` state
   *  (analysis-service/services/optimization/problem_builder.py:203,
   *  `initial_state["warnings"] = warnings_list`) — same element shape as
   *  OptimizationCompleteEventSchema.warnings. `extract_problem_state` never
   *  sets it, so it is absent from complete states and from the whole Zemax
   *  track — but the global-algorithm (DE/DA/BH) progress path does forward it
   *  for a while: `GlobalAlgorithmRun` seeds `best_state` with a deep copy of
   *  that same init state (analysis-service/routers/optimization.py:97) and
   *  `_emit_progress` deep-copies `best_state` onto the wire (:113), so
   *  early `optimization_progress` frames still carry the init warnings until
   *  the first improving generation swaps `best_state` for a fresh
   *  `extract_problem_state` (:148-152). The LS-inner progress path builds
   *  each payload from `extract_problem_state` directly
   *  (services/optimization/ls_inner_step.py `_build_progress_payload`) and so
   *  never carries it. */
  warnings: z.array(z.object({ field: z.string(), message: z.string(), severity: z.string() })).optional(),
  /** Zemax init-state only: the `optimize-step/stream` init frame carries
   *  the run's method/algorithm before the worker call even starts
   *  (zemax-analysis-service/routers/optimization.py:486-487). Absent from
   *  progress/complete states (`_build_optimization_state` doesn't set
   *  them) and from the whole Optiland track. */
  method: z.string().optional(),
  algorithm: z.string().optional(),
});

export const QuestionOptionSchema = z.object({
  label: z.string(),
  description: z.string(),
});

export const AgentQuestionSchema = z.object({
  question: z.string(),
  header: z.string(),
  options: z.array(QuestionOptionSchema),
  multiSelect: z.boolean(),
});

/** One proposed parallel branch: a short label plus the self-contained plan
 *  the branch agent runs. Shared by the branch_proposal event and the
 *  persisted record. */
export const BranchOptionSchema = z.object({
  label: z.string(),
  instruction: z.string(),
});

/** A resolved interactive exchange (AskUserQuestion, a confirmation prompt, or
 *  a branch proposal), persisted as a chat_messages row so it survives reload.
 *  The exchange is complete by the time this is recorded — it is a static
 *  record, not a live control. `answers` is keyed by AgentQuestion.question;
 *  branch fields carry the agent's proposed set and the user's final
 *  (possibly edited) choice. */
export const AgentInteractionRecordSchema = z.object({
  kind: z.enum(['question', 'confirmation', 'branch']),
  status: z.enum(['answered', 'declined', 'timeout']),
  questions: z.array(AgentQuestionSchema).optional(),
  answers: z.record(z.string(), z.string()).optional(),
  decisionName: z.string().optional(),
  proposed: z.array(BranchOptionSchema).optional(),
  chosen: z.array(BranchOptionSchema).optional(),
});

export const CITATION_SOURCE_KINDS = ['firmFile', 'template', 'playbook'] as const;

/** Firm knowledge the agent actively used during a turn, surfaced as a
 *  citation chip in the chat. `scope` mirrors PlaybookScope
 *  (services/firmPlaybooks/types.ts), inlined here rather than imported —
 *  contracts cannot import from src/. */
export const CitationSourceSchema = z.object({
  kind: z.enum(CITATION_SOURCE_KINDS),
  name: z.string(), // display name
  scope: z.enum(['personal', 'org', 'starter']), // PlaybookScope
  ref: z.string().optional(), // firmFile: manifest ref slug
  id: z.string().optional(), // template / playbook: DB id
});

export type DeployVersionEvent = z.infer<typeof DeployVersionEventSchema>;
export type OptimizationOperand = z.infer<typeof OptimizationOperandSchema>;
export type OptimizationVariable = z.infer<typeof OptimizationVariableSchema>;
export type OptimizationMerit = z.infer<typeof OptimizationMeritSchema>;
export type OptimizationState = z.infer<typeof OptimizationStateSchema>;
export type QuestionOption = z.infer<typeof QuestionOptionSchema>;
export type AgentQuestion = z.infer<typeof AgentQuestionSchema>;
export type BranchOption = z.infer<typeof BranchOptionSchema>;
export type AgentInteractionRecord = z.infer<typeof AgentInteractionRecordSchema>;
export type CitationSource = z.infer<typeof CitationSourceSchema>;
export type CitationSourceKind = (typeof CITATION_SOURCE_KINDS)[number];

/** Identity of a cited source: firmFile always has a ref, template and playbook
 *  always an id, and name is the last-resort tiebreaker. Lives here so the chip's
 *  deep link and the per-bubble dedupe cannot drift apart. */
export const citationSourceKey = (s: CitationSource): string =>
  `${s.kind}:${s.ref ?? s.id ?? s.name}`;
